/**
 * «Разбор Айланы» — что у ассистента спрашивали и как он отвечал.
 *
 * Экран под одну работу: раз в какое-то время просмотреть накопившееся и по
 * нему чинить промпт и инструменты. Поэтому здесь не «журнал вообще», а
 * ПОРЦИЯ С ПОСЛЕДНЕЙ ПРОВЕРКИ: сверху числа по ней, кнопка отдаёт её файлом.
 *
 * ⚠️ Просмотр списка отметку НЕ ставит — иначе нельзя просто полистать. Курсор
 * двигают только «Выгрузить и отметить» и «Отметить проверенным».
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Card, DatePicker, Drawer, Dropdown, Empty, Input, Popconfirm,
  Segmented, Select, Space, Table, Tabs, Tag, Tooltip, Typography, message,
} from 'antd';
import {
  CheckOutlined, DownOutlined, DownloadOutlined, HistoryOutlined,
  MessageOutlined, ReloadOutlined, RobotOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { type Dayjs } from 'dayjs';
import { PageHeader, StatCard, StatRow } from '../../components/shared';
import { L } from '../../styles/theme';
import {
  assistantApi, type AssistantLogRow, type LogDetail, type LogsQuery,
  type OnlyFilter, type Overview, type ToolCall,
} from '../../api/assistant';
import { DefectsTab } from './DefectsTab';

const { Text, Paragraph } = Typography;

const ONLY_OPTIONS: { value: OnlyFilter | ''; label: string }[] = [
  { value: '', label: 'Все ответы' },
  { value: 'errors', label: 'Только сбои' },
  { value: 'down', label: 'Оценка 👎' },
  { value: 'up', label: 'Оценка 👍' },
  { value: 'rated', label: 'С любой оценкой' },
  { value: 'slow', label: 'Медленные' },
  { value: 'no_tools', label: 'Без инструментов' },
  { value: 'cache', label: 'Ответ из кэша' },
];

const fmtDt = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'medium' }) : '—';

const fmtSec = (ms?: number | null) => (ms == null ? '—' : `${(ms / 1000).toFixed(1)} с`);

const cut = (s: string | null, n: number) =>
  !s ? '' : s.length > n ? `${s.slice(0, n)}…` : s;

/** Скачать файл, полученный ручкой выгрузки. */
function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Один вызов инструмента в подробностях: имя, аргументы, что вернул. */
const CallBlock: React.FC<{ c: ToolCall }> = ({ c }) => (
  <div style={{ borderTop: `1px solid ${L.lineSoft}`, padding: '10px 0' }}>
    <Space size={6} wrap>
      <Tag style={{ margin: 0, fontFamily: 'var(--l-mono, monospace)' }}>{c.tool}</Tag>
      <Text type="secondary" style={{ fontSize: 12 }}>{c.ms ?? '?'} мс</Text>
      {c.via ? <Tag color="orange">позвала страховка: {c.via}</Tag> : null}
    </Space>
    <pre style={{
      margin: '8px 0 0', padding: 10, background: L.fill, borderRadius: 6,
      fontFamily: L.mono, fontSize: 11.5, lineHeight: 1.5, whiteSpace: 'pre-wrap',
      wordBreak: 'break-word', maxHeight: 260, overflow: 'auto', color: L.text,
    }}>
      {`аргументы: ${JSON.stringify(c.args ?? {}, null, 1)}\n\nвернул: ${JSON.stringify(c.result, null, 1)}`}
    </pre>
  </div>
);

/** Одна реплика в подробностях (и в цепочке разговора). */
const Turn: React.FC<{ row: AssistantLogRow; dim?: boolean }> = ({ row, dim }) => (
  <div style={{ opacity: dim ? 0.6 : 1, marginBottom: 18 }}>
    <Space size={6} wrap style={{ marginBottom: 6 }}>
      <Text style={{ fontFamily: L.mono, fontSize: 12, color: L.faint }}>#{row.id}</Text>
      <Text type="secondary" style={{ fontSize: 12 }}>{fmtDt(row.created_at)}</Text>
      <Text type="secondary" style={{ fontSize: 12 }}>{fmtSec(row.latency_ms)}</Text>
      {row.error ? <Tag color="error">сбой</Tag> : null}
      {row.feedback === -1 ? <Tag>👎</Tag> : null}
      {row.feedback === 1 ? <Tag>👍</Tag> : null}
      {row.cache_hit ? <Tag>из кэша</Tag> : null}
      {(row.safety_nets || []).map((s) => <Tag key={s} color="orange">{s}</Tag>)}
    </Space>
    <div style={{
      background: L.accentBg, borderRadius: 8, padding: '8px 12px', marginBottom: 8,
      color: L.ink, fontWeight: 500,
    }}>{row.message}</div>
    <div style={{ whiteSpace: 'pre-wrap', color: L.text }}>
      {row.answer || <Text type="secondary">— ответа нет —</Text>}
    </div>
    {row.error ? (
      <Alert type="error" showIcon style={{ marginTop: 8 }} message={row.error} />
    ) : null}
    {row.calls?.length ? (
      <div style={{ marginTop: 10 }}>
        <Text strong style={{ fontSize: 12 }}>Инструменты</Text>
        {row.calls.map((c, i) => <CallBlock key={i} c={c} />)}
      </div>
    ) : row.tools?.length ? (
      <div style={{ marginTop: 10 }}>
        <Text strong style={{ fontSize: 12 }}>Инструменты: </Text>
        <Text>{row.tools.join(', ')}</Text>
        <Text type="secondary" style={{ fontSize: 12 }}> (старый формат лога — аргументов нет)</Text>
      </div>
    ) : (
      <div style={{ marginTop: 10 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Инструменты не звались — отвечала из головы.
        </Text>
      </div>
    )}
    <div style={{ marginTop: 10, fontSize: 12, color: L.faint }}>
      {`язык ${row.lang || '—'} · гео ${row.geo ? 'есть' : 'нет'} · итераций ${row.iterations ?? '—'}`}
      {row.tokens ? ` · токены ${row.tokens.prompt ?? '?'}/${row.tokens.completion ?? '?'} (из кэша ${row.tokens.cache_hit ?? 0})` : ''}
      {row.llm_ms != null ? ` · модель ${row.llm_ms} мс, инструменты ${row.tools_ms ?? 0} мс` : ''}
    </div>
  </div>
);

const AssistantLogs: React.FC = () => {
  const [ov, setOv] = useState<Overview | null>(null);
  const [rows, setRows] = useState<AssistantLogRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState<LogDetail | null>(null);

  // Фильтры
  const [scope, setScope] = useState<'fresh' | 'all'>('fresh');
  const [only, setOnly] = useState<OnlyFilter | ''>('');
  const [tool, setTool] = useState<string | undefined>();
  const [q, setQ] = useState('');
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const query = useMemo<LogsQuery>(() => ({
    since_last_review: scope === 'fresh',
    only: only || undefined,
    tool: tool || undefined,
    q: q.trim() || undefined,
    date_from: range?.[0] ? range[0].startOf('day').toISOString() : undefined,
    date_to: range?.[1] ? range[1].endOf('day').toISOString() : undefined,
  }), [scope, only, tool, q, range]);

  const loadOverview = useCallback(async () => {
    try {
      setOv(await assistantApi.overview());
    } catch {
      message.error('Не удалось получить сводку по Айлане');
    }
  }, []);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await assistantApi.logs({
        ...query, limit: pageSize, offset: (page - 1) * pageSize,
      });
      setRows(res.items);
      setTotal(res.total);
    } catch {
      message.error('Не удалось получить диалоги');
    } finally {
      setLoading(false);
    }
  }, [query, page, pageSize]);

  useEffect(() => { loadOverview(); }, [loadOverview]);
  useEffect(() => { loadRows(); }, [loadRows]);
  useEffect(() => { setPage(1); }, [query]);

  const doExport = useCallback(async (fmt: 'md' | 'jsonl', mark: boolean) => {
    setBusy(true);
    try {
      // ⚠️ С отметкой выгружается ВСЯ порция с последней проверки, а не то, что
      // сейчас в фильтрах: курсор — одно число, и под фильтром остальные строки
      // ушли бы за него молча. Сервер такую отметку и не примет.
      const params = mark
        ? { since_last_review: true, fmt, mark: true }
        : { ...query, fmt, mark: false };
      const res = await assistantApi.export(params);
      if (res.count === 0) {
        message.info('Выгружать нечего: с последней проверки новых диалогов нет');
        return;
      }
      saveBlob(res.blob, res.filename);
      message.success(
        mark
          ? `Выгружено ${res.count} — отмечено проверенным до строки #${res.lastId}`
          : `Выгружено ${res.count} (по текущим фильтрам, отметка не ставилась)`,
      );
      if (mark) { await loadOverview(); await loadRows(); }
    } catch {
      message.error('Выгрузка не получилась');
    } finally {
      setBusy(false);
    }
  }, [query, loadOverview, loadRows]);

  const markReviewed = useCallback(async () => {
    const lastId = ov?.fresh.max_log_id;
    if (!lastId || !ov?.fresh.count) {
      message.info('Отмечать нечего: новых диалогов нет');
      return;
    }
    setBusy(true);
    try {
      await assistantApi.mark(lastId, ov.fresh.count, 'без выгрузки');
      message.success(`Отмечено проверенным до строки #${lastId}`);
      await loadOverview(); await loadRows();
    } catch {
      message.error('Не удалось поставить отметку');
    } finally {
      setBusy(false);
    }
  }, [ov, loadOverview, loadRows]);

  const undoMark = useCallback(async () => {
    if (!ov?.last_review) return;
    setBusy(true);
    try {
      await assistantApi.dropMark(ov.last_review.id);
      message.success('Отметка снята — курсор вернулся на предыдущую');
      await loadOverview(); await loadRows();
    } catch {
      message.error('Не удалось снять отметку');
    } finally {
      setBusy(false);
    }
  }, [ov, loadOverview, loadRows]);

  const openDetail = useCallback(async (id: number) => {
    try {
      setDetail(await assistantApi.detail(id));
    } catch {
      message.error('Не удалось открыть диалог');
    }
  }, []);

  const slowMs = ov?.slow_ms ?? 8000;

  const columns: ColumnsType<AssistantLogRow> = [
    {
      title: 'Когда', dataIndex: 'created_at', width: 150,
      render: (v: string, r) => (
        <div>
          <div style={{ fontSize: 12.5 }}>{fmtDt(v)}</div>
          <Text style={{ fontFamily: L.mono, fontSize: 11, color: L.faint }}>#{r.id}</Text>
        </div>
      ),
    },
    {
      title: 'Вопрос и ответ', dataIndex: 'message',
      render: (_: string, r) => (
        <div style={{ minWidth: 260 }}>
          <div style={{ color: L.ink, fontWeight: 500, marginBottom: 2 }}>{r.message}</div>
          <div style={{ color: L.muted, fontSize: 12.5 }}>
            {r.answer ? cut(r.answer.replace(/\n+/g, ' '), 160) : '— ответа нет —'}
          </div>
        </div>
      ),
    },
    {
      title: 'Инструменты', dataIndex: 'tools', width: 200,
      render: (tools: string[]) => (
        tools?.length
          ? <Space size={4} wrap>{tools.map((t) => (
              <Tag key={t} style={{ marginInlineEnd: 0 }}
                   >{t}</Tag>
            ))}</Space>
          : <Text type="secondary" style={{ fontSize: 12 }}>из головы</Text>
      ),
    },
    {
      title: 'Время', dataIndex: 'latency_ms', width: 90, align: 'right',
      sorter: (a, b) => (a.latency_ms || 0) - (b.latency_ms || 0),
      render: (v: number | null) => (
        <Text style={{ color: (v || 0) >= slowMs ? L.warn : L.text, fontSize: 12.5 }}>
          {fmtSec(v)}
        </Text>
      ),
    },
    {
      title: 'Метки', width: 140,
      render: (_: unknown, r) => (
        <Space size={4} wrap>
          {r.error ? <Tag color="error" style={{ marginInlineEnd: 0 }}>сбой</Tag> : null}
          {r.feedback === -1 ? <Tag style={{ marginInlineEnd: 0 }}>👎</Tag> : null}
          {r.feedback === 1 ? <Tag style={{ marginInlineEnd: 0 }}>👍</Tag> : null}
          {r.cache_hit ? <Tag style={{ marginInlineEnd: 0 }}>кэш</Tag> : null}
          {r.safety_nets?.length
            ? <Tooltip title={r.safety_nets.join(', ')}>
                <Tag color="orange" style={{ marginInlineEnd: 0 }}>страховка</Tag>
              </Tooltip>
            : null}
          {r.session_id
            ? <Tooltip title={`Разговор ${r.session_id}`}>
                <Tag style={{ marginInlineEnd: 0 }}>разговор</Tag>
              </Tooltip>
            : null}
        </Space>
      ),
    },
    {
      title: 'Житель', dataIndex: 'user_name', width: 150,
      render: (v: string | null) => (
        <Text style={{ fontSize: 12.5, color: v ? L.text : L.faint }}>{v || 'гость'}</Text>
      ),
    },
  ];

  const fresh = ov?.fresh;

  return (
    <div>
      <PageHeader
        title="Разбор Айланы"
        icon={<RobotOutlined />}
        subtitle={ov ? `всего в логе ${ov.total_all}` : undefined}
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => { loadOverview(); loadRows(); }}>
              Обновить
            </Button>
            <Dropdown
              menu={{
                items: [
                  { key: 'md', label: 'Выгрузить .md по фильтрам (без отметки)' },
                  { key: 'jsonl-mark', label: 'Выгрузить .jsonl и отметить' },
                  { key: 'jsonl', label: 'Выгрузить .jsonl по фильтрам (без отметки)' },
                  { type: 'divider' as const },
                  { key: 'mark', label: 'Отметить проверенным (без файла)' },
                  ...(ov?.last_review
                    ? [{ key: 'undo', label: 'Снять последнюю отметку', danger: true }]
                    : []),
                ],
                onClick: ({ key }) => {
                  if (key === 'md') doExport('md', false);
                  else if (key === 'jsonl-mark') doExport('jsonl', true);
                  else if (key === 'jsonl') doExport('jsonl', false);
                  else if (key === 'mark') markReviewed();
                  else if (key === 'undo') undoMark();
                },
              }}
            >
              <Button icon={<DownOutlined />} loading={busy} />
            </Dropdown>
            <Tooltip title="Файл .md со всеми диалогами с прошлой проверки — с вопросами, ответами и вызовами инструментов. Ставит отметку «просмотрено». Фильтры на неё не влияют: отметка всегда по всей порции.">
              <Button
                type="primary" icon={<DownloadOutlined />} loading={busy}
                onClick={() => doExport('md', true)}
              >
                Выгрузить с последней проверки
              </Button>
            </Tooltip>
          </Space>
        }
      />

      {/* ── ДВЕ СТОРОНЫ ОДНОГО РАЗБОРА ────────────────────────────────────
          «Диалоги» — то, что смотрит человек. «Дефекты» — то, что нашла машина
          ночью, пока никто не смотрел (сторож, заход 7 от 17.09.2026). Это
          вкладка, а не отдельная страница: работа одна и та же. */}
      <Tabs
        defaultActiveKey="logs"
        items={[
          { key: 'logs', label: 'Диалоги', children: (<>
      <StatRow>
        <StatCard label="С последней проверки" value={fresh?.count ?? '—'}
                  icon={<MessageOutlined />}
                  hint={ov?.last_review
                    ? `проверено ${fmtDt(ov.last_review.checked_at)}`
                    : 'проверок ещё не было'} />
        <StatCard label="Сбоев" value={fresh?.errors ?? '—'} color={L.danger}
                  colorValue={!!fresh?.errors}
                  onClick={() => { setScope('fresh'); setOnly('errors'); }} />
        <StatCard label="Оценка 👎" value={fresh?.thumbs_down ?? '—'} color={L.warn}
                  colorValue={!!fresh?.thumbs_down}
                  onClick={() => { setScope('fresh'); setOnly('down'); }} />
        <StatCard label="Без инструментов" value={fresh?.no_tools ?? '—'}
                  hint="отвечала из головы"
                  onClick={() => { setScope('fresh'); setOnly('no_tools'); }} />
        <StatCard label={`Дольше ${Math.round(slowMs / 1000)} с`} value={fresh?.slow ?? '—'}
                  onClick={() => { setScope('fresh'); setOnly('slow'); }} />
        <StatCard label="Медиана ответа" value={fresh?.p50_ms ? fmtSec(fresh.p50_ms) : '—'}
                  hint={fresh?.p95_ms ? `95-й процентиль ${fmtSec(fresh.p95_ms)}` : undefined} />
      </StatRow>

      {ov && ov.fresh.count === 0 ? (
        <Alert
          style={{ marginTop: 12 }} type="success" showIcon
          message="Новых диалогов с последней проверки нет"
          description={ov.last_review
            ? `Последняя проверка: ${fmtDt(ov.last_review.checked_at)}, до строки #${ov.last_review.last_log_id}.`
            : undefined}
        />
      ) : null}

      <Card size="small" style={{ marginTop: 12 }} styles={{ body: { padding: 12 } }}>
        <Space wrap size={8}>
          <Segmented
            value={scope}
            onChange={(v) => setScope(v as 'fresh' | 'all')}
            options={[
              { label: 'С последней проверки', value: 'fresh' },
              { label: 'Весь лог', value: 'all' },
            ]}
          />
          <Select
            value={only} onChange={setOnly} style={{ width: 190 }}
            options={ONLY_OPTIONS}
          />
          <Select
            allowClear placeholder="Инструмент" value={tool} onChange={setTool}
            style={{ width: 190 }}
            options={(ov?.fresh.tools || []).map((t) => ({
              value: t.name, label: `${t.name} · ${t.n}`,
            }))}
          />
          <Input.Search
            allowClear placeholder="Искать в вопросах и ответах" style={{ width: 260 }}
            onSearch={setQ}
          />
          <DatePicker.RangePicker
            value={range as any} onChange={(v) => setRange(v as any)}
            format="DD.MM.YYYY"
          />
        </Space>
      </Card>

      <Card size="small" style={{ marginTop: 12 }} styles={{ body: { padding: 0 } }}>
        <Table<AssistantLogRow>
          rowKey="id"
          size="small"
          loading={loading}
          columns={columns}
          dataSource={rows}
          onRow={(r) => ({ onClick: () => openDetail(r.id), style: { cursor: 'pointer' } })}
          locale={{ emptyText: <Empty description="Диалогов под эти условия нет" /> }}
          pagination={{
            current: page, pageSize, total, showSizeChanger: true,
            pageSizeOptions: [25, 50, 100, 200],
            showTotal: (t) => `всего ${t}`,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); },
          }}
        />
      </Card>

      {ov?.reviews?.length ? (
        <Card
          size="small" style={{ marginTop: 12 }}
          title={<Space><HistoryOutlined />Проверки</Space>}
        >
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            {ov.reviews.map((r) => (
              <div key={r.id} style={{ fontSize: 12.5, color: L.muted }}>
                {fmtDt(r.checked_at)} — до строки #{r.last_log_id}
                {r.count != null ? `, диалогов ${r.count}` : ''}
                {r.admin_name ? `, ${r.admin_name}` : ''}
                {r.note ? ` (${r.note})` : ''}
              </div>
            ))}
          </Space>
        </Card>
      ) : null}

          </>) },
          {
            key: 'defects',
            label: 'Дефекты',
            children: <DefectsTab onOpenLog={openDetail} />,
          },
        ]}
      />

      <Drawer
        open={!!detail} width={760} onClose={() => setDetail(null)}
        title={detail ? `Диалог #${detail.item.id}` : ''}
        extra={detail ? (
          <Popconfirm
            title="Отметить проверенным до этой строки?"
            onConfirm={async () => {
              await assistantApi.mark(detail.item.id, undefined, 'вручную по строке');
              message.success(`Отмечено до #${detail.item.id}`);
              setDetail(null); loadOverview(); loadRows();
            }}
          >
            <Button icon={<CheckOutlined />}>Отметить до этой строки</Button>
          </Popconfirm>
        ) : null}
      >
        {detail ? (
          <>
            {detail.session.length > 1 ? (
              <Alert
                type={detail.session_guessed ? 'warning' : 'info'} showIcon
                style={{ marginBottom: 16 }}
                message={detail.session_guessed
                  ? 'Разговор собран по догадке'
                  : `Разговор: ${detail.session.length} вопросов`}
                description={detail.session_guessed
                  ? 'У этой строки нет id разговора (старая сборка приложения). Показаны соседние вопросы того же жителя в пределах получаса — это может быть и другой разговор.'
                  : undefined}
              />
            ) : null}
            {detail.session.length > 1
              ? detail.session.map((r) => (
                  <Turn key={r.id} row={r} dim={r.id !== detail.item.id} />
                ))
              : <Turn row={detail.item} />}
            <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8 }}>
              {detail.item.user_name
                ? `Житель: ${detail.item.user_name}`
                : 'Житель: гость (без входа)'}
              {detail.item.session_id ? ` · разговор ${detail.item.session_id}` : ''}
            </Paragraph>
          </>
        ) : null}
      </Drawer>
    </div>
  );
};

export default AssistantLogs;
